import { useEffect, useRef } from 'react'

const REVEALED = 'is-revealed'
const SETTLED = 'is-settled'

/**
 * Reveals `.reveal` descendants as they scroll into view.
 *
 * Returns a ref to attach to a container; every `.reveal` inside it is
 * observed and staggered in document order. Elements are unobserved once
 * they land, so this never re-hides content the user has already read.
 *
 * @param {object}  options
 * @param {number}  options.stagger  ms between siblings (default 70)
 * @param {number}  options.maxSteps cap on the stagger ladder, so a long
 *                                   list does not end up waiting seconds
 */
const useReveal = ({ stagger = 70, maxSteps = 6 } = {}) => {
    const containerRef = useRef(null)

    useEffect(() => {
        const root = containerRef.current
        if (!root) return

        const targets = Array.from(root.querySelectorAll('.reveal'))
        if (!targets.length) return

        const prefersReduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

        // No IntersectionObserver (or the user asked for stillness): show
        // everything immediately rather than leaving it stuck at opacity 0.
        if (prefersReduced || typeof IntersectionObserver === 'undefined') {
            targets.forEach((el) => el.classList.add(REVEALED, SETTLED))
            return
        }

        // Stagger is assigned per batch, so items entering together ripple
        // in order while a lone item further down reveals with no delay.
        const observer = new IntersectionObserver((entries, obs) => {
            entries
                .filter((entry) => entry.isIntersecting)
                .forEach((entry, i) => {
                    const el = entry.target
                    const step = Math.min(i, maxSteps)
                    el.style.setProperty('--reveal-delay', `${step * stagger}ms`)
                    el.classList.add(REVEALED)

                    // Drop the will-change promotion once it has landed
                    const settle = () => el.classList.add(SETTLED)
                    el.addEventListener('transitionend', settle, { once: true })

                    obs.unobserve(el)
                })
        }, {
            threshold: 0.15,
            rootMargin: '0px 0px -12% 0px'
        })

        targets.forEach((el) => observer.observe(el))

        return () => observer.disconnect()
    }, [stagger, maxSteps])

    return containerRef
}

export default useReveal
